import React, { useState } from "react";
import { ModalConfirm, TextInput, UI_BODY, UI_BODY_SM, UI_CAPTION, colors2 } from "oolib";
import { ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import { SM_UIContent } from "../SM_UIContent";

/**
 * Type-to-confirm modal for dataset deletion. Handles single or bulk via
 * the `targets` array — user types the dataset id(s) verbatim (in the
 * order shown, comma-separated for bulk) to enable the destructive action.
 */
export const DeleteDatasetConfirmModal = ({
  targets,
  skipped = 0,
  onConfirm,
  onClose,
  // When the caller keeps the modal mounted across the delete request (rather
  // than closing on confirm + showing a full-screen loader), pass the
  // mutation's pending state so the confirm button disables and shows progress
  // until it resolves. Defaults to false → no behaviour change for callers
  // that unmount the modal on confirm.
  isDeleting = false,
}) => {
  const list = (Array.isArray(targets) ? targets : [targets]).filter(Boolean);
  const count = list.length;
  const isBulk = count > 1;

  // What the user must type. For bulk it's a comma-separated list of ids
  // — same source of truth as the single case (kp_content_type).
  const requiredText = list.map((t) => t.kp_content_type).join(", ");
  // Human-friendly subtitle.
  const titles = list
    .map((t) => t?.general?.content?.title || t.kp_content_type)
    .join(", ");

  const [typed, setTyped] = useState("");
  const matched = typed.trim() === requiredText && requiredText.length > 0;
  const copy = SM_UIContent.SM_Datasets.confirmDeleteDataset;

  const title = isBulk ? `Delete ${count} datasets?` : copy.title;
  const onConfirmText = isBulk ? "Delete datasets" : copy.onConfirmText;
  const warning = isBulk
    ? `This will permanently delete these ${count} datasets and ALL associated data: every document, annotation, AI categorization, topic, summary, chunk, embedding, search index, role permission and the template configurations themselves. This action cannot be undone.`
    : copy.warning;
  const prompt = isBulk
    ? "Type the dataset ids (comma-separated, in order) to confirm:"
    : copy.typeToConfirmPrompt;

  return (
    <ModalConfirm
      title={title}
      subTitle={titles}
      onClose={onClose}
      showConfirmBtn={false}
      onConfirm={() => {}}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <UI_BODY color="red">{warning}</UI_BODY>
        <UI_BODY_SM>
          {prompt}{" "}
          <span style={{ fontWeight: 600, color: colors2.grey90 }}>
            {requiredText}
          </span>
        </UI_BODY_SM>
        <TextInput
          value={typed}
          onChange={(k, v) => setTyped(v)}
          placeholder={requiredText}
          forceFocus
        />
        {skipped > 0 && (
          <UI_CAPTION color="warningColor">
            {skipped} selected dataset{skipped > 1 ? "s are" : " is"} importing
            and will be skipped.
          </UI_CAPTION>
        )}
        <div>
          <ButtonPrimary
            onClick={onConfirm}
            disabled={!matched || isDeleting}
          >
            {isDeleting ? "Deleting…" : onConfirmText}
          </ButtonPrimary>
        </div>
      </div>
    </ModalConfirm>
  );
};
